import { useState } from "react";
import { PageHeader, Disclaimer } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle2, Upload, FileText, MapPin } from "lucide-react";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const STEPS = [
  "Personal Details",
  "Current Visa",
  "Education in Australia",
  "Full Education History",
  "Employment History",
  "Skills & Occupation",
  "English Language",
  "PR Points Factors",
  "Sponsorship & Nomination",
  "Immigration History",
  "Journey in Australia",
  "Future Intentions",
  "Declaration",
];

const YES_NO = [{ v: "Yes", l: "Yes" }, { v: "No", l: "No" }, { v: "Unsure", l: "Unsure" }];

const initial = {
  // Personal
  full_name: "", email: "", phone: "", date_of_birth: "", nationality: "",
  passport_number: "", marital_status: "", partner_details: "",
  // Current visa
  current_visa_subclass: "", visa_grant_date: "", visa_expiry_date: "",
  visa_conditions: "", previous_visas: "",
  // Education in Australia
  current_course: "", institution_name: "", course_dates: "",
  two_academic_years: "", regional_study: "",
  completed_principal_course: "", incomplete_reason: "",
  // Full education
  highest_qualification: "", full_study_history: "", study_gaps: "",
  // Employment
  current_employment: "", previous_employment: "", employment_gaps: "",
  job_related_to_study: "",
  // Skills
  intended_occupation: "", occupation_on_skilled_list: "",
  skills_assessment_completed: "", skills_assessment_details: "",
  // English
  english_test_type: "", english_test_score: "", english_test_date: "",
  // Points
  age: "", naati_credentialed: "", partner_skills: "", additional_points: "",
  // Sponsorship
  currently_employed: "", employer_name: "", employer_willing_to_sponsor: "",
  preferred_state: "", interested_regional: "",
  // Immigration
  visa_refusals: "", visa_cancellations: "", breaches_overstays: "",
  warnings_notices: "",
  // Journey
  first_arrival: "", all_courses_in_australia: "", course_institution_changes: "",
  journey_explanation: "",
  // Future
  preferred_pr_pathway: "", planned_timeline: "", career_goals: "",
  // Files
  cv_file_path: "", cv_filename: "",
  consent: false,
};

export default function StudentPRAssessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setStep((s) => Math.max(s - 1, 0)); };

  const uploadCV = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await apiClient.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("cv_file_path", res.path);
      update("cv_filename", res.filename);
      toast.success("CV uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!data.consent) {
      toast.error("Please confirm the declaration to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        age: data.age ? parseInt(data.age) : null,
        consent_timestamp: new Date().toISOString(),
      };
      await apiClient.post("/student-pr-assessment", payload);
      setDone(true);
      toast.success("Assessment submitted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHeader eyebrow="Submitted" title="Thank you — your Student PR assessment is in." subtitle="One of our Registered Migration Agents will personally review your details and respond by email within 2 business days with suitable PR pathways, estimated points, risk analysis and next steps." />
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#C43A89] mx-auto mb-6" />
            <h2 className="font-display text-3xl text-[#29324A]">What you can expect</h2>
            <ul className="mt-8 text-left space-y-4 text-slate-700 max-w-2xl mx-auto">
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">·</span><span>Suitable PR pathways for your profile.</span></li>
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">·</span><span>Estimated points calculation.</span></li>
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">·</span><span>Risk and gap analysis.</span></li>
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">·</span><span>Strategic next steps tailored to you.</span></li>
            </ul>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="student-pr-page">
      <PageHeader
        eyebrow={<><MapPin className="inline h-3.5 w-3.5 mr-1" /> Onshore Applicants in Australia</>}
        title="Onshore Visa Assessment."
        subtitle="A detailed assessment designed for current visa holders in Australia — students, graduates, temporary workers and others planning their permanent residency pathway. Every migration journey is unique."
      />

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Purpose & Compliance notice */}
          {step === 0 && (
            <div className="bg-[#F3F2F0] border-l-4 border-[#29324A] p-5 mb-10 text-sm text-slate-700 leading-relaxed">
              <strong className="text-[#29324A] block mb-1.5">Purpose & Compliance</strong>
              In accordance with the Migration Agents Code of Conduct, we collect complete and accurate information to ensure all advice is lawful, appropriate and tailored to your circumstances. All information is treated with <strong>strict confidentiality</strong>.
            </div>
          )}

          {/* Stepper */}
          <div className="mb-10">
            <div className="flex items-center justify-between text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">
              <span data-testid="student-pr-step">Step {step + 1} / {STEPS.length}: <span className="text-[#29324A]">{STEPS[step]}</span></span>
              <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5 bg-slate-100" />
          </div>

          {/* Step content */}
          <div className="space-y-5 fade-up" key={step}>
            {step === 0 && (
              <>
                <Section title="1. Personal Details" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name (as per passport) *"><Input data-testid="spr-full_name" value={data.full_name} onChange={(e) => update("full_name", e.target.value)} /></Field>
                  <Field label="Date of birth"><Input data-testid="spr-dob" type="date" value={data.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} /></Field>
                  <Field label="Email *"><Input data-testid="spr-email" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} /></Field>
                  <Field label="Phone *"><Input data-testid="spr-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                  <Field label="Nationality"><Input data-testid="spr-nationality" value={data.nationality} onChange={(e) => update("nationality", e.target.value)} /></Field>
                  <Field label="Passport number"><Input data-testid="spr-passport" value={data.passport_number} onChange={(e) => update("passport_number", e.target.value)} /></Field>
                  <Field label="Marital status">
                    <Select value={data.marital_status} onValueChange={(v) => update("marital_status", v)}>
                      <SelectTrigger data-testid="spr-marital"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married">Married / De-facto</SelectItem>
                        <SelectItem value="separated">Separated</SelectItem>
                        <SelectItem value="divorced">Divorced</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Partner details (if applicable)"><Textarea data-testid="spr-partner" rows={3} value={data.partner_details} onChange={(e) => update("partner_details", e.target.value)} placeholder="Age, nationality, qualifications, English level, visa status" /></Field>
              </>
            )}

            {step === 1 && (
              <>
                <Section title="2. Current Visa Details" />
                <Field label="Current visa subclass & details"><Input data-testid="spr-visa-subclass" value={data.current_visa_subclass} onChange={(e) => update("current_visa_subclass", e.target.value)} placeholder="e.g. Subclass 500 Student" /></Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Visa grant date"><Input data-testid="spr-grant-date" type="date" value={data.visa_grant_date} onChange={(e) => update("visa_grant_date", e.target.value)} /></Field>
                  <Field label="Visa expiry date"><Input data-testid="spr-expiry-date" type="date" value={data.visa_expiry_date} onChange={(e) => update("visa_expiry_date", e.target.value)} /></Field>
                </div>
                <Field label="Any visa conditions applied?"><Textarea data-testid="spr-conditions" rows={2} value={data.visa_conditions} onChange={(e) => update("visa_conditions", e.target.value)} placeholder="e.g. 8202, 8501, 8533" /></Field>
                <Field label="Previous visas held in Australia"><Textarea data-testid="spr-prev-visas" rows={3} value={data.previous_visas} onChange={(e) => update("previous_visas", e.target.value)} placeholder="Subclass, dates, key details" /></Field>
              </>
            )}

            {step === 2 && (
              <>
                <Section title="3. Education in Australia" />
                <Field label="Current course"><Input data-testid="spr-course" value={data.current_course} onChange={(e) => update("current_course", e.target.value)} /></Field>
                <Field label="Institution name"><Input data-testid="spr-institution" value={data.institution_name} onChange={(e) => update("institution_name", e.target.value)} /></Field>
                <Field label="Course start & expected end date"><Input data-testid="spr-course-dates" value={data.course_dates} onChange={(e) => update("course_dates", e.target.value)} placeholder="e.g. Feb 2024 — Dec 2025" /></Field>
                <div className="grid sm:grid-cols-3 gap-5">
                  <YesNoField label="Completed at least 2 academic years in Australia?" testId="spr-two-years" value={data.two_academic_years} onChange={(v) => update("two_academic_years", v)} />
                  <YesNoField label="Studied in a regional area?" testId="spr-regional" value={data.regional_study} onChange={(v) => update("regional_study", v)} />
                  <YesNoField label="Completed principal course?" testId="spr-principal" value={data.completed_principal_course} onChange={(v) => update("completed_principal_course", v)} />
                </div>
                <Field label="If NOT completed, explain reasons"><Textarea data-testid="spr-incomplete" rows={3} value={data.incomplete_reason} onChange={(e) => update("incomplete_reason", e.target.value)} /></Field>
              </>
            )}

            {step === 3 && (
              <>
                <Section title="4. Full Education History" />
                <Field label="Highest qualification completed (include country & year)"><Textarea data-testid="spr-qualification" rows={2} value={data.highest_qualification} onChange={(e) => update("highest_qualification", e.target.value)} /></Field>
                <Field label="Complete study history (Australia + overseas)"><Textarea data-testid="spr-study-history" rows={5} value={data.full_study_history} onChange={(e) => update("full_study_history", e.target.value)} placeholder="List qualifications, institutions, countries and dates" /></Field>
                <Field label="Any gaps in study (include dates & explanation)"><Textarea data-testid="spr-study-gaps" rows={3} value={data.study_gaps} onChange={(e) => update("study_gaps", e.target.value)} /></Field>
              </>
            )}

            {step === 4 && (
              <>
                <Section title="5. Employment History" />
                <Field label="Current employment (role, employer, start date)"><Textarea data-testid="spr-current-emp" rows={3} value={data.current_employment} onChange={(e) => update("current_employment", e.target.value)} /></Field>
                <Field label="Previous employment (Australia + overseas)"><Textarea data-testid="spr-prev-emp" rows={5} value={data.previous_employment} onChange={(e) => update("previous_employment", e.target.value)} placeholder="Role, employer, country, dates, hours per week" /></Field>
                <Field label="Any gaps in employment (include dates & explanation)"><Textarea data-testid="spr-emp-gaps" rows={3} value={data.employment_gaps} onChange={(e) => update("employment_gaps", e.target.value)} /></Field>
                <YesNoField label="Is your current job related to your field of study?" testId="spr-job-related" value={data.job_related_to_study} onChange={(v) => update("job_related_to_study", v)} />
              </>
            )}

            {step === 5 && (
              <>
                <Section title="6. Skills & Occupation" />
                <Field label="Intended occupation for PR"><Input data-testid="spr-intended-occ" value={data.intended_occupation} onChange={(e) => update("intended_occupation", e.target.value)} placeholder="e.g. ANZSCO 261313 Software Engineer" /></Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <YesNoField label="Occupation on Skilled List (if known)?" testId="spr-on-list" value={data.occupation_on_skilled_list} onChange={(v) => update("occupation_on_skilled_list", v)} />
                  <YesNoField label="Skills assessment completed?" testId="spr-skills-done" value={data.skills_assessment_completed} onChange={(v) => update("skills_assessment_completed", v)} />
                </div>
                <Field label="If yes, provide details"><Textarea data-testid="spr-skills-details" rows={3} value={data.skills_assessment_details} onChange={(e) => update("skills_assessment_details", e.target.value)} placeholder="Assessing authority, outcome, date" /></Field>
              </>
            )}

            {step === 6 && (
              <>
                <Section title="7. English Language" />
                <div className="grid sm:grid-cols-3 gap-5">
                  <Field label="Test type">
                    <Select value={data.english_test_type} onValueChange={(v) => update("english_test_type", v)}>
                      <SelectTrigger data-testid="spr-english-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IELTS">IELTS</SelectItem>
                        <SelectItem value="PTE">PTE Academic</SelectItem>
                        <SelectItem value="TOEFL iBT">TOEFL iBT</SelectItem>
                        <SelectItem value="OET">OET</SelectItem>
                        <SelectItem value="Cambridge">Cambridge</SelectItem>
                        <SelectItem value="None">Not taken</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Score"><Input data-testid="spr-english-score" value={data.english_test_score} onChange={(e) => update("english_test_score", e.target.value)} placeholder="e.g. 7.5 / 79 / 65" /></Field>
                  <Field label="Test date"><Input data-testid="spr-english-date" type="date" value={data.english_test_date} onChange={(e) => update("english_test_date", e.target.value)} /></Field>
                </div>
              </>
            )}

            {step === 7 && (
              <>
                <Section title="8. PR Points Factors" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Age"><Input data-testid="spr-age" type="number" value={data.age} onChange={(e) => update("age", e.target.value)} /></Field>
                  <YesNoField label="NAATI / Credentialed community language?" testId="spr-naati" value={data.naati_credentialed} onChange={(v) => update("naati_credentialed", v)} />
                  <YesNoField label="Partner skills applicable?" testId="spr-partner-skills" value={data.partner_skills} onChange={(v) => update("partner_skills", v)} />
                </div>
                <Field label="Any additional points factors"><Textarea data-testid="spr-additional-points" rows={3} value={data.additional_points} onChange={(e) => update("additional_points", e.target.value)} placeholder="Professional year, regional study, specialist education, etc." /></Field>
              </>
            )}

            {step === 8 && (
              <>
                <Section title="9. Employer Sponsorship & State Nomination" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <YesNoField label="Currently employed in Australia?" testId="spr-currently-emp" value={data.currently_employed} onChange={(v) => update("currently_employed", v)} />
                  <Field label="Employer name (if applicable)"><Input data-testid="spr-employer-name" value={data.employer_name} onChange={(e) => update("employer_name", e.target.value)} /></Field>
                  <YesNoField label="Employer willing to sponsor?" testId="spr-sponsor" value={data.employer_willing_to_sponsor} onChange={(v) => update("employer_willing_to_sponsor", v)} />
                </div>

                <div className="pt-4">
                  <h4 className="font-display text-lg text-[#29324A] mb-3">State nomination</h4>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Preferred state/territory">
                      <Select value={data.preferred_state} onValueChange={(v) => update("preferred_state", v)}>
                        <SelectTrigger data-testid="spr-state"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NSW">New South Wales</SelectItem>
                          <SelectItem value="VIC">Victoria</SelectItem>
                          <SelectItem value="QLD">Queensland</SelectItem>
                          <SelectItem value="WA">Western Australia</SelectItem>
                          <SelectItem value="SA">South Australia</SelectItem>
                          <SelectItem value="TAS">Tasmania</SelectItem>
                          <SelectItem value="ACT">ACT</SelectItem>
                          <SelectItem value="NT">Northern Territory</SelectItem>
                          <SelectItem value="any">Any state</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <YesNoField label="Interested in regional pathways?" testId="spr-regional-interest" value={data.interested_regional} onChange={(v) => update("interested_regional", v)} />
                  </div>
                </div>
              </>
            )}

            {step === 9 && (
              <>
                <Section title="10. Immigration History" />
                <div className="grid sm:grid-cols-3 gap-5">
                  <YesNoField label="Any visa refusals?" testId="spr-refusals" value={data.visa_refusals} onChange={(v) => update("visa_refusals", v)} />
                  <YesNoField label="Any visa cancellations?" testId="spr-cancellations" value={data.visa_cancellations} onChange={(v) => update("visa_cancellations", v)} />
                  <YesNoField label="Any breaches or overstays?" testId="spr-breaches" value={data.breaches_overstays} onChange={(v) => update("breaches_overstays", v)} />
                </div>
                <Field label="Any warnings/notices regarding visa or course compliance"><Textarea data-testid="spr-warnings" rows={3} value={data.warnings_notices} onChange={(e) => update("warnings_notices", e.target.value)} /></Field>
              </>
            )}

            {step === 10 && (
              <>
                <Section title="11. Long-Term Stay & Journey in Australia" />
                <Field label="When did you first arrive in Australia? (month/year)"><Input data-testid="spr-arrival" value={data.first_arrival} onChange={(e) => update("first_arrival", e.target.value)} placeholder="e.g. February 2022" /></Field>
                <Field label="List all courses undertaken in Australia with outcomes"><Textarea data-testid="spr-all-courses" rows={5} value={data.all_courses_in_australia} onChange={(e) => update("all_courses_in_australia", e.target.value)} placeholder="Course, institution, dates, outcome" /></Field>
                <Field label="Any course or institution changes? Provide details"><Textarea data-testid="spr-changes" rows={3} value={data.course_institution_changes} onChange={(e) => update("course_institution_changes", e.target.value)} /></Field>
                <Field label="Explain your full journey in Australia (study, work, changes)"><Textarea data-testid="spr-journey" rows={6} value={data.journey_explanation} onChange={(e) => update("journey_explanation", e.target.value)} placeholder="Tell us your story — context helps us tailor advice." /></Field>
              </>
            )}

            {step === 11 && (
              <>
                <Section title="12. Future Intentions" />
                <Field label="Preferred PR pathway (if known)">
                  <Select value={data.preferred_pr_pathway} onValueChange={(v) => update("preferred_pr_pathway", v)}>
                    <SelectTrigger data-testid="spr-preferred-pathway"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="189">Subclass 189 — Skilled Independent</SelectItem>
                      <SelectItem value="190">Subclass 190 — State Nominated</SelectItem>
                      <SelectItem value="491">Subclass 491 — Regional</SelectItem>
                      <SelectItem value="482">Subclass 482 — Skills in Demand</SelectItem>
                      <SelectItem value="186">Subclass 186 — ENS</SelectItem>
                      <SelectItem value="494">Subclass 494 — Regional Employer</SelectItem>
                      <SelectItem value="partner">Partner Visa</SelectItem>
                      <SelectItem value="485">Subclass 485 — Graduate first</SelectItem>
                      <SelectItem value="unsure">Unsure — please advise</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Planned timeline for PR application"><Input data-testid="spr-timeline" value={data.planned_timeline} onChange={(e) => update("planned_timeline", e.target.value)} placeholder="e.g. Within 12 months" /></Field>
                <Field label="Career goals and long-term plans"><Textarea data-testid="spr-career" rows={4} value={data.career_goals} onChange={(e) => update("career_goals", e.target.value)} /></Field>

                <Field label="Upload CV (PDF/DOC, max 10 MB) — optional but recommended">
                  <label className="border-2 border-dashed border-slate-300 hover:border-[#29324A] p-6 flex flex-col items-center justify-center cursor-pointer transition-colors text-center">
                    <Upload className="h-7 w-7 text-[#29324A] mb-3" />
                    <span className="text-sm font-semibold text-[#29324A]">
                      {data.cv_filename ? data.cv_filename : "Click to upload your CV"}
                    </span>
                    <span className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX — up to 10 MB</span>
                    <input data-testid="spr-cv" type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => uploadCV(e.target.files?.[0])} />
                  </label>
                  {uploading && <div className="text-xs text-slate-500 mt-2">Uploading...</div>}
                  {data.cv_filename && !uploading && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700"><FileText className="h-4 w-4" /> {data.cv_filename}</div>
                  )}
                </Field>
              </>
            )}

            {step === 12 && (
              <>
                <Section title="13. Declaration & Consent" />
                <p className="text-sm text-slate-700 leading-relaxed">
                  I declare that the information provided is true, correct and complete. I understand that providing incorrect or misleading information may affect my assessment outcome. I consent to the use of this information for the purpose of assessing my eligibility for Australian migration pathways in accordance with applicable laws and professional obligations.
                </p>

                <div className="bg-[#F8F9FA] border border-slate-200 p-5 space-y-3 text-sm">
                  <div className="font-display text-lg text-[#29324A]">What you can expect</div>
                  <p className="text-slate-600 leading-relaxed">Upon receiving your completed form, our team will provide a comprehensive assessment including:</p>
                  <ul className="space-y-1.5 text-slate-700">
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Suitable PR pathways</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Estimated points calculation</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Risk and gap analysis</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Strategic next steps</li>
                  </ul>
                </div>

                <Disclaimer>
                  This assessment is general guidance only. It does not constitute personalised migration advice and does not guarantee any visa outcome. For tailored advice, please book a paid consultation.
                </Disclaimer>

                <ConsentCheckbox
                  value={data.consent}
                  onChange={(v) => update("consent", v)}
                  testid="spr-consent"
                  showDisclaimer={false}
                />
              </>
            )}
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between gap-3 mt-12 pt-6 border-t border-slate-200">
            <Button data-testid="spr-back-btn" type="button" onClick={back} disabled={step === 0} className="bg-transparent border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white disabled:opacity-30 rounded-none px-6 py-3">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="spr-next-btn" type="button" onClick={next} className="bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none px-6 py-3">
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button data-testid="spr-submit-btn" type="button" onClick={submit} disabled={submitting || !data.consent} className="bg-[#C43A89] hover:bg-[#a22b6e] text-white rounded-none px-6 py-3 disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Assessment"}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function YesNoField({ label, value, onChange, testId }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          {YES_NO.map((o) => (<SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function Section({ title, className = "" }) {
  return (
    <div className={`flex items-center gap-3 pb-2 border-b border-slate-200 ${className}`}>
      <span className="brand-dot bg-[#C43A89]" />
      <h3 className="font-display text-2xl text-[#29324A]">{title}</h3>
    </div>
  );
}
